import type moment from 'moment'

import asyncAction from 'modules/core/lib/asyncAction'
import { isSuccessful } from 'modules/core/lib/asyncAction'
import { dateToString } from 'modules/core/lib/dates'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useRefreshShiftsRows } from 'modules/shift_management/hooks/refreshShiftsRows'
import { DROP_ACTIONS } from 'modules/shift_management/hooks/useDragAndDrop'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import { ADD_SHIFT_OPTIONS } from 'modules/shift_management/lib/addShiftOptions'
import { trackAddShiftSubmitEvent } from 'modules/shift_management/lib/trackEvents'
import { ShiftsRow } from 'modules/shift_management/lib/types'
import shiftsCollection from 'modules/shift_management/store/api/collections/shifts'
import Shift from 'modules/shift_management/store/internal/models/Shift'

export interface CopyShiftProps {
  originShiftsRow: ShiftsRow
  newShiftsRow: ShiftsRow
  shift: Shift
  date: moment.Moment
  employeeId: number
  contractVersions: ContractVersions
}

const useCopyShift = (contractVersions: ContractVersions) => {
  const [, dispatchShiftsRows] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchUI] = useUI()

  const { refreshShiftsRows } = useRefreshShiftsRows(contractVersions)

  const copyShift = async ({
    originShiftsRow,
    newShiftsRow,
    shift,
    date,
    employeeId,
  }: CopyShiftProps) => {
    const formattedDate = dateToString(date, 'backend')
    const result = await asyncAction(() => {
      return shiftsCollection.rpc('drop', {
        id: shift.id,
        employee_id: employeeId,
        date: formattedDate,
        drop_action: DROP_ACTIONS.copy,
      })
    })

    if (isSuccessful(result)) {
      trackAddShiftSubmitEvent({
        view,
        employeeIds: [shift.employeeId],
        shiftsCount: 1,
        restDaysCount: 0,
        shiftType: ADD_SHIFT_OPTIONS.duplicate,
        color: shift.color,
        from: 'drag_and_drop',
      })

      const newShift = Shift.buildFromRequest(result.value)
      dispatchUI({ type: 'REFRESHING_STATS' })
      dispatchSchedule({ type: 'ADD_SHIFTS', shifts: [newShift] })
      dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts: [newShift], view })

      // Get all shift rows that have shifts by employees affected by the edit action to update stats
      await refreshShiftsRows([originShiftsRow, newShiftsRow], [shift.employeeId, employeeId])
    }
  }

  return { copyShift }
}

export default useCopyShift
